"use client"

import React, { createContext, useContext, useState, ReactNode, useCallback } from "react"

import { CalculatedProductPrice } from "@lib/util/get-product-price"
import { CheckoutExperimentsDefinition } from "@modules/signup-and-order-flow/experiments/experiment-interfaces"

// --- Storage Keys ---
const SESSION_STORAGE_KEY = "signup-and-order-session"

interface PersistedSessionData {
  user: {
    userUuid: string | null
  }
  progress: {
    paymentOptionsCompleted: boolean
    signupCompleted: boolean
    hasOrderBeenPlaced: boolean
    serverErrorOccurred: boolean
  }
  loan: {
    applicationStatus: any | null
  }
}

const saveSessionToStorage = (sessionData: SessionData) => {
  if (typeof window === "undefined") return
  try {
    const dataToStore: PersistedSessionData = {
      user: {
        userUuid: sessionData.user.userUuid,
      },
      progress: {
        paymentOptionsCompleted: sessionData.progress.paymentOptionsCompleted,
        signupCompleted: sessionData.progress.signupCompleted,
        hasOrderBeenPlaced: sessionData.progress.hasOrderBeenPlaced,
        serverErrorOccurred: sessionData.progress.serverErrorOccurred,
      },
      loan: {
        applicationStatus: sessionData.loan.applicationStatus,
      },
    }
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(dataToStore))
  } catch (error) {
    console.error("Error saving session to storage:", error)
  }
}

const loadSessionFromStorage = (): Partial<PersistedSessionData> | null => {
  if (typeof window === "undefined") return null
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error("Error loading session from storage:", error)
  }
  return null
}

export interface SessionData {
  product: {
    productId: string | null
    calculatedPrices: CalculatedProductPrice | null
    preSelectedInstallment?: number
    cartTotal?: number | null
  }

  user: {
    userUuid: string | null
  }

  progress: {
    paymentOptionsCompleted: boolean
    signupCompleted: boolean
    hasOrderBeenPlaced: boolean
    serverErrorOccurred: boolean
  }

  loan: {
    applicationStatus?: any | null
  }

  treatments: CheckoutExperimentsDefinition
}

interface SessionContextValue {
  sessionData: SessionData
  updateSession: (updates: Partial<SessionData>) => void
}

const SessionContext = createContext<SessionContextValue | null>(null)

export const useSession = () => {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error("useSession must be used within SessionProvider")
  }
  return context
}

// --- Provider Component ---

interface SessionProviderProps {
  children: ReactNode
  treatments: CheckoutExperimentsDefinition
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children, treatments }) => {
  const [sessionData, setSessionData] = useState<SessionData>(() => {
    const storedSessionData = loadSessionFromStorage()

    const initialSession: SessionData = {
      product: {
        productId: null,
        calculatedPrices: null,
        preSelectedInstallment: undefined,
        cartTotal: null,
      },
      user: {
        userUuid: null,
      },
      progress: {
        paymentOptionsCompleted: false,
        signupCompleted: false,
        hasOrderBeenPlaced: false,
        serverErrorOccurred: false,
      },
      loan: {
        applicationStatus: null,
      },
      treatments,
    }

    if (storedSessionData) {
      initialSession.progress = storedSessionData.progress || initialSession.progress
      initialSession.loan = storedSessionData.loan || initialSession.loan
      if (storedSessionData.user) {
        initialSession.user.userUuid = storedSessionData.user.userUuid
      }
    }

    return initialSession
  })

  const updateSession = useCallback((updates: Partial<SessionData>) => {
    setSessionData((prev: SessionData) => {
      const newData = { ...prev, ...updates }
      // Save the rest of the session data to localStorage
      saveSessionToStorage(newData)
      return newData
    })
  }, [])

  return <SessionContext.Provider value={{ sessionData, updateSession }}>{children}</SessionContext.Provider>
}
